<template>
  <li class="cart-item">
    <div class="cart-item__header">
      <div class="cart-item__header-name">{{ getNameByLocale(menu) }}</div>
      <div class="cart-item__header-remove" @click="handleRemoveClick">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.075 25.075">
          <path
            fill="#b8b8b8"
            d="M24.258,100.207a2.509,2.509,0,1,0-3.548-3.548l-8.247,8.255L4.207,96.667a2.509,2.509,0,1,0-3.548,3.548l8.255,8.247L.667,116.718a2.509,2.509,0,1,0,3.548,3.548l8.247-8.255,8.255,8.247a2.509,2.509,0,1,0,3.548-3.548l-8.255-8.247Z"
            transform="translate(0.075 -95.925)"
          />
        </svg>
      </div>
    </div>
    <div class="cart-item__options">{{ optionsNames ? `ㄴ${optionsNames}` : '' }}</div>
    <div class="cart-item__footer">
      <div class="cart-item__footer-quantity">
        <BaseCounter
          v-model="cartMenu.Qty"
          class="small"
          :disabled="false"
          :min="minQty"
          :limit="limitCount"
          @update:model-value="updateQty"
        />
      </div>
      <div class="cart-item__footer-price">{{ formatCurrency(totalAmountByMenu) }}</div>
    </div>
  </li>
</template>

<script lang="ts" setup>
  import BaseCounter from '@/components/base/BaseCounter.vue';
  import { useMenu } from '@/composable/useMenu';
  import CartMenu from '@/interfaces/CartMenu';
  import { useCartStore } from '@/stores/cart';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { formatCurrency, getNameByLocale } from '@/utils';
  import { computed } from 'vue';

  const props = defineProps<{
    /** 장바구니 메뉴 인덱스 */
    index: number;
    /** 장바구니 메뉴 데이터 */
    cartMenu: CartMenu;
  }>();

  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const cartStore = useCartStore();

  const { calcCartMinOrderQty } = useMenu();

  // 메뉴 데이터
  const menu = computed(() => {
    return menuStore.getMenu(props.cartMenu.GroupCode, props.cartMenu.ItemCode);
  });

  // 최소 주문 수량
  const minQty = computed(() => calcCartMinOrderQty(menu.value));

  // 한정 수량
  const limitCount = computed(() => menuStore.getLimitItem(props.cartMenu.ItemCode)?.OrderLimitCnt);

  // 선택한 토핑/세트메뉴 이름들
  const optionsNames = computed(() => {
    if (props.cartMenu.Toppings.length > 0) {
      return props.cartMenu.Toppings.reduce((acc, option) => {
        const prefix = acc ? `${acc} / ` : '';
        const topping = menuStore.getTopping(option.GroupCode.toString(), option.OptionCode);
        const name = getNameByLocale(topping);
        const price = menuStore.getToppingPrice(topping, menu.value.IsTax2);
        const priceText = price > 0 ? ` (+${price * option.Qty})` : '';
        const qtyText = option.Qty > 1 ? ` x${option.Qty}` : '';

        return `${prefix}${name}${qtyText}${priceText}`;
      }, '');
    } else if (props.cartMenu.SetMenus.length > 0) {
      return props.cartMenu.SetMenus.reduce((acc, option) => {
        const prefix = acc ? `${acc} / ` : '';
        const setMenu = menuStore.getSetMenu(option.MenuItemCode, Number(option.GroupCode), option.OptionCode);
        const name = getNameByLocale(setMenu);
        const price = menuStore.getSetMenuPrice(setMenu, menu.value.IsTax2);
        const qtyText = option.Qty > 1 ? ` x${option.Qty}` : '';
        const priceText = price > 0 ? ` (+${price * option.Qty})` : '';

        return `${prefix}${name}${qtyText}${priceText}`;
      }, '');
    }
    return '';
  });

  // 해당 메뉴의 총 금액
  const totalAmountByMenu = computed(() =>
    cartStore.getCartMenuPrice(props.cartMenu, props.cartMenu.Qty, configStore.config.IsTaxIncluded),
  );

  const handleRemoveClick = () => {
    cartStore.removeCartMenu(props.cartMenu);
  };

  const updateQty = (value: number) => {
    cartStore.setQty(props.index, value);
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .cart-item {
    display: flex;
    flex-direction: column;
    padding: 0.7813vw;

    &__header {
      display: flex;
      flex-direction: row;
      align-items: center;

      &-name {
        flex: 1;
        color: var(--text-contrast-color);
        font-size: 1.6418vw;
        font-weight: 700;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        @media (max-width: vars.$lg) {
          font-size: 20px;
        }
        @media (max-width: vars.$md) {
          font-size: 14px;
        }
      }

      &-remove {
        display: flex;
        align-items: center;
        padding: 0.3906vw;
        cursor: pointer;
        svg {
          width: 1.5625vw;
          @media (max-width: vars.$lg) {
            font-size: 20px;
          }
          @media (max-width: vars.$md) {
            font-size: 14px;
          }
        }
      }
    }

    &__options {
      margin-top: 0.3125vw;
      font-size: 1.2687vw;
      color: var(--text-secondary);
      text-overflow: ellipsis;
      overflow: hidden;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;

      @media (max-width: vars.$lg) {
        font-size: 16px;
      }
      @media (max-width: vars.$md) {
        font-size: 12px;
      }

      &:empty {
        display: none;
      }
    }

    &__footer {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 1.1719vw;

      &-price {
        flex: 1;
        color: var(--text-contrast-color);
        text-align: right;
        font-size: 1.6418vw;

        @media (max-width: vars.$lg) {
          font-size: 20px;
        }
        @media (max-width: vars.$md) {
          font-size: 14px;
        }
      }
    }
  }
</style>
